import countries from 'i18n-iso-countries';
import { useEffect, useRef, useState } from 'react';
import { MessageDescriptor } from 'react-intl';
import axios, { AxiosError } from 'axios';
import ErrorInterface from '../interfaces/Error';
import AppNameEnum from '../enums/AppNameEnum';
import { FetchedStage, Stage, QuestionnaireKey } from '../components/App/App';

export function isRunningInIFrame() {
	return window.top !== window;
}

export function isMobile() {
	return window.innerWidth < 768;
}
export function isMobileApp() {
	if (typeof window === undefined) {
		return false;
	}
	const { navigator }: { navigator: Navigator & { standalone?: boolean } } = window;
	const { standalone } = navigator;
	const userAgent = navigator.userAgent.toLowerCase();
	const safari = /safari/.test(userAgent);
	const ios = /iphone|ipod|ipad/.test(userAgent);

	return ios ? !standalone && !safari : userAgent.includes('wv');
}

export function isDeviceIOS() {
	return (
		/iPad|iPhone|iPod/.test(navigator.userAgent) ||
		(navigator.userAgent === 'MacIntel' && navigator.maxTouchPoints > 1)
	);
}

export function isFirefoxOnAndroid() {
	const userAgent = navigator.userAgent.toLowerCase();
	return userAgent.includes('firefox') && userAgent.includes('android');
}

export function isDeviceMacOrIOS() {
	return /(macintosh|macintel|macppc|mac68k|macos|iphone|ipad|ipod)/i.test(
		window.navigator.userAgent.toLowerCase()
	);
}

export function getPreferredCountries(country: string) {
	return country ? countries.alpha3ToAlpha2(country).toLowerCase() : 'gb';
}

export function useTraceUpdate(props: any) {
	const prev = useRef(props);
	useEffect(() => {
		const changedProps = Object.entries(props).reduce((ps: any, [k, v]) => {
			const newPs = ps;
			if (prev.current[k] !== v) {
				newPs[k] = [prev.current[k], v];
			}
			return newPs;
		}, {});
		prev.current = props;
	});
}

export function isValueEmpty(x: any) {
	return (
		typeof x === 'undefined' ||
		x === null ||
		x === false ||
		x.length === 0 ||
		x === 0 ||
		x === '' ||
		x.replace(/\s/g, '') === '' ||
		!/[^\s]/.test(x) ||
		/^\s*$/.test(x)
	);
}

// functional components only
export function useDebounce(value: string, delay: number) {
	const [debouncedValue, setDebouncedValue] = useState<string>(value);

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value);
		}, delay);
		return () => {
			clearTimeout(handler);
		};
	}, [delay, value]);

	return debouncedValue;
}

export const getValueTranslationIdFromValue = (value: string) => {
	const values: { [key: string]: MessageDescriptor } = {
		confirmationOfTransaction: {
			id: 'base.confirmation_of_transaction',
			defaultMessage: 'Confirmation of high value transaction'
		},
		uboVerification: {
			id: 'base.identification_of_ubo',
			defaultMessage: 'Identity verification of Ultimate Beneficial Owner'
		},
		representativeVerification: {
			id: 'base.identification_of_representative',
			defaultMessage: "Identity verification of Company's Representative"
		},
		additionalUserVerification: {
			id: 'base.identification_of_additional_user',
			defaultMessage: "Identity verification of additional Company's account user"
		},
		passwordRecovery: {
			id: 'base.password_recovery',
			defaultMessage: 'Password recovery'
		},
		signatory: {
			id: 'base.signatory',
			defaultMessage: 'Signatory'
		},
		trader: {
			id: 'base.trader',
			defaultMessage: 'Trader'
		},
		officer: {
			id: 'base.officer',
			defaultMessage: 'Officer'
		},
		admin: {
			id: 'base.admin_secretarial',
			defaultMessage: 'Admin/Secretarial'
		},
		accountancy: {
			id: 'base.accountancy',
			defaultMessage: 'Accountancy'
		},
		agriculture: {
			id: 'base.agriculture',
			defaultMessage: 'Agriculture'
		},
		financial: {
			id: 'base.financial_services_banking',
			defaultMessage: 'Financial Services - Banking'
		},
		catering: {
			id: 'base.catering_hospitality',
			defaultMessage: 'Catering/Hospitality'
		},
		creative: {
			id: 'base.creative_media',
			defaultMessage: 'Creative/Media'
		},
		education: {
			id: 'base.education',
			defaultMessage: 'Education'
		},
		emergency: {
			id: 'base.emergency_services',
			defaultMessage: 'Emergency Services'
		},
		engineering: {
			id: 'base.engineering',
			defaultMessage: 'Engineering'
		},
		financialServicesOther: {
			id: 'base.financial_services_other',
			defaultMessage: 'Financial Services - Other'
		},
		health: {
			id: 'base.health_medicine',
			defaultMessage: 'Health/Medicine'
		},
		HMForces: {
			id: 'base.hm_forces',
			defaultMessage: 'HM Forces'
		},
		hr: {
			id: 'base.hr',
			defaultMessage: 'HR'
		},
		financialServicesInsurance: {
			id: 'base.financial_services_insurance',
			defaultMessage: 'Financial Services - Insurance'
		},
		it: {
			id: 'base.it',
			defaultMessage: 'IT'
		},
		legal: {
			id: 'base.legal',
			defaultMessage: 'Legal'
		},
		leisure: {
			id: 'base.leisure_entertainment_tourism',
			defaultMessage: 'Leisure/Entertainment/Tourism'
		},
		manufacturing: {
			id: 'base.manufacturing',
			defaultMessage: 'Manufacturing'
		},
		marketing: {
			id: 'base.marketing_pr_advertising',
			defaultMessage: 'Marketing/PR/Advertising'
		},
		pharmaceuticals: {
			id: 'base.pharmaceuticals',
			defaultMessage: 'Pharmaceuticals'
		},
		property: {
			id: 'base.property_constructions_trades',
			defaultMessage: 'Property/Constructions/Trades'
		},
		retail: {
			id: 'base.retail',
			defaultMessage: 'Retail'
		},
		option1: {
			id: 'base.high_school',
			defaultMessage: 'High school'
		},
		option2: {
			id: 'base.secondary',
			defaultMessage: 'Secondary'
		},
		option3: {
			id: 'base.bachelors_degree',
			defaultMessage: "Bachelor's degree"
		},
		option4: {
			id: 'base.masters_degree',
			defaultMessage: "Master's degree"
		},
		option5: {
			id: 'base.doctorate',
			defaultMessage: 'Doctorate'
		},
		employed: {
			id: 'base.employed',
			defaultMessage: 'Employed'
		},
		selfEmployed: {
			id: 'base.self_employed',
			defaultMessage: 'Self-employed'
		},
		unemployed: {
			id: 'base.unemployed',
			defaultMessage: 'Un-employed'
		},
		retired: {
			id: 'base.retired',
			defaultMessage: 'Retired'
		},
		pension: {
			id: 'base.pension',
			defaultMessage: 'Pension'
		},
		zero: {
			id: 'base.0',
			defaultMessage: '0'
		},
		lessThanOne: {
			id: 'base.less_than_1',
			defaultMessage: '<1'
		},
		oneTwo: {
			id: 'base.between_1_and_2',
			defaultMessage: '1-2'
		},
		moreThanTwo: {
			id: 'base.more_than_2',
			defaultMessage: '>2'
		},
		lessThan10: {
			id: 'base.less_than_10',
			defaultMessage: 'Less than 10'
		},
		tenTwenty: {
			id: 'base.between_10_and_20',
			defaultMessage: '10-20'
		},
		twentyFourty: {
			id: 'base.between_20_and_40',
			defaultMessage: '20-40'
		},
		moreThanFourty: {
			id: 'base.more_than_40',
			defaultMessage: '>40'
		},
		none: {
			id: 'base.none',
			defaultMessage: 'None'
		},
		sectorOption1: { id: 'base.sector_accounting', defaultMessage: 'ACCOUNTING' },
		sectorOption2: {
			id: 'base.sector_activities_related_to_cultural_artifacts',
			defaultMessage: 'ART_RELATED_ACTIVITIES'
		},
		sectorOption3: {
			id: 'base.sector_jewellery_activities',
			defaultMessage: 'JEWELLERY_ACTIVITIES'
		},
		sectorOption4: {
			id: 'base.sector_virtual_currencies_activities',
			defaultMessage: 'VIRTUAL_CURRENCY_ACTIVITIES'
		},
		sectorOption5: {
			id: 'base.sector_agriculture_forestry_fishery',
			defaultMessage: 'AGRICULTURE_FORESTRY_FISHERY'
		},
		sectorOption6: { id: 'base.sector_audit', defaultMessage: 'AUDIT' },
		sectorOption7: { id: 'base.sector_aviation', defaultMessage: 'AVIATION' },
		sectorOption8: { id: 'base.sector_charity', defaultMessage: 'CHARITY' },
		sectorOption9: { id: 'base.sector_chemical_industry', defaultMessage: 'CHEMICAL_INDUSTRY' },
		sectorOption10: {
			id: 'base.sector_corporate_services_provided',
			defaultMessage: 'CORPORATE_SERVICES_PROVIDER'
		},
		sectorOption11: {
			id: 'base.sector_currency_exchange',
			defaultMessage: 'CURRENCY_EXCHANGE'
		},
		sectorOption12: { id: 'base.sector_e_commerce', defaultMessage: 'E_COMMERCE' },
		sectorOption13: { id: 'base.sector_education', defaultMessage: 'EDUCATION' },
		sectorOption14: {
			id: 'base.sector_extraction_of_natural_resourses',
			defaultMessage: 'NATURAL_RESOURCES'
		},
		sectorOption15: {
			id: 'base.sector_film_music_photography_art_culture',
			defaultMessage: 'FILM_MUSIC_PHOTOGRAPHY'
		},
		sectorOption16: { id: 'base.gambling_and_lotteries', defaultMessage: 'GAMBLING_LOTTERIES' },
		sectorOption17: {
			id: 'base.sector_health_and_social_care',
			defaultMessage: 'HEALTH_AND_SOCIAL_CARE'
		},
		sectorOption18: {
			id: 'base.sector_holding_activities',
			defaultMessage: 'HOLDING_ACTIVITIES'
		},
		sectorOption19: {
			id: 'base.sector_hotels_restaurants',
			defaultMessage: 'HOTELS_RESTAURANTS'
		},
		sectorOption20: {
			id: 'base.sector_information_technology',
			defaultMessage: 'INFORMATION_TECHNOLOGY'
		},
		sectorOption21: { id: 'base.sector_insurance', defaultMessage: 'INSURANCE' },
		sectorOption22: {
			id: 'base.sector_issuance_of_electronic_money',
			defaultMessage: 'ELECTRONIC_MONEY_ISSUANCE'
		},
		sectorOption23: { id: 'base.sector_legal_services', defaultMessage: 'LEGAL_SERVICES' },
		sectorOption24: { id: 'base.sector_manufaturing', defaultMessage: 'MANUFACTURING' },
		sectorOption25: {
			id: 'base.sector_marketing_and_affiliate',
			defaultMessage: 'MARKETING_AND_AFFILIATE'
		},
		sectorOption26: { id: 'base.sector_non_profit', defaultMessage: 'NON_PROFIT' },
		sectorOption27: { id: 'base.sector_nuclear_energy', defaultMessage: 'NUCLEAR_ENERGY' },
		sectorOption28: {
			id: 'base.sector_operating_auctions',
			defaultMessage: 'OPERATING_AUCTIONS'
		},
		sectorOption29: {
			id: 'base.sector_other_financial_services',
			defaultMessage: 'OTHER_FINANCIAL_SERVICES'
		},
		sectorOption30: {
			id: 'base.sector_payment_operations',
			defaultMessage: 'PAYMENT_OPERATIONS'
		},
		sectorOption31: {
			id: 'base.sector_pharmaceutical_business',
			defaultMessage: 'PHARMACEUTICAL_BUSINESS'
		},
		sectorOption32: { id: 'base.sector_sports', defaultMessage: 'SPORTS' },
		sectorOption33: {
			id: 'base.sector_public_administration_and_defence',
			defaultMessage: 'PUBLIC_ADMINISTRATION_AND_DEFENCE'
		},
		sectorOption34: { id: 'base.sector_real_estate', defaultMessage: 'REAL_ESTATE' },
		sectorOption35: {
			id: 'base.sector_telecommunications',
			defaultMessage: 'TELECOMMUNICATIONS'
		},
		sectorOption36: { id: 'base.sector_tourism_and_travel', defaultMessage: 'TOURISM' },
		sectorOption37: { id: 'base.sector_trade_in_arms', defaultMessage: 'TRADE_IN_ARMS' },
		sectorOption38: {
			id: 'base.sector_trade_in_transport',
			defaultMessage: 'TRADE_IN_TRANSPORT'
		},
		sectorOption39: { id: 'base.sector_trade_in_oil', defaultMessage: 'TRADE_IN_OIL' },
		sectorOption40: { id: 'base.sector_trade_in_tobacco', defaultMessage: 'TRADE_IN_TOBACCO' },
		sectorOption41: {
			id: 'base.sector_transport_logistics',
			defaultMessage: 'TRANSPORT_LOGISTICS'
		},
		sectorOption42: { id: 'base.sector_trust_services', defaultMessage: 'TRUST_SERVICES' },
		sectorOption43: { id: 'base.other', defaultMessage: 'OTHER' }
	};

	let translationId = null;
	Object.entries(values).forEach(([, translation]) => {
		if (translation.defaultMessage === value) {
			translationId = translation.id;
		}
	});

	return translationId;
};

export const getBnkPrivate2023SectorValueTranslationIdFromValue = (value: string) => {
	const values: { [key: string]: MessageDescriptor } = {
		accounting: { id: 'base.sector_accounting', defaultMessage: 'ACCOUNTING' },
		virtualCurrencies: {
			id: 'base.sector_crypto_and_virtual_currencies_activities',
			defaultMessage: 'VIRTUAL_CURRENCY_ACTIVITIES'
		},
		agricultureCattleBreedingForestryFishery: {
			id: 'base.sector_agriculture_cattle_breeding_forestry_fishery',
			defaultMessage: 'AGRICULTURE_FORESTRY_FISHERY'
		},
		audit: { id: 'base.sector_audit', defaultMessage: 'AUDIT' },
		aviation: { id: 'base.sector_aviation', defaultMessage: 'AVIATION' },
		eCommerceActivities: {
			id: 'base.sector_e_commerce_activities',
			defaultMessage: 'E_COMMERCE'
		},
		education: { id: 'base.sector_education', defaultMessage: 'EDUCATION' },
		filmMusicPhotographyOtherActivities: {
			id: 'base.sector_film_music_photography_art_culture_other_activities',
			defaultMessage: 'FILM_MUSIC_PHOTOGRAPHY'
		},
		activePartakingInGamblingAndLotteries: {
			id: 'base.active_partaking_in_gambling_and_lotteries',
			defaultMessage: 'GAMBLING_LOTTERIES'
		},
		informationTechnology: {
			id: 'base.sector_information_technology',
			defaultMessage: 'INFORMATION_TECHNOLOGY'
		},
		legalActivities: { id: 'base.sector_legal_activities', defaultMessage: 'LEGAL_SERVICES' },
		referralAndAffiliateMarketing: {
			id: 'base.sector_referral_and_affiliate_marketing',
			defaultMessage: 'MARKETING_AND_AFFILIATE'
		},
		professionalSportsActivities: {
			id: 'base.sector_professional_sports_activities',
			defaultMessage: 'SPORTS'
		},
		realEstateBrokerageActivities: {
			id: 'base.sector_real_estate_brokerage_activities',
			defaultMessage: 'REAL_ESTATE_BROKERAGE'
		},
		telecommunications: {
			id: 'base.sector_telecommunications',
			defaultMessage: 'TELECOMMUNICATIONS'
		},
		investmentActivitiesInStockAndOtherFinancialMarkets: {
			id: 'base.investment_activities_in_stock_and_other_financial_markets',
			defaultMessage: 'INVESTMENT_IN_STOCK_AND_OTHER_FINANCIAL_MARKETS'
		},
		activitiesOfNotaryPublic: {
			id: 'base.activities_of_notary_public',
			defaultMessage: 'ACTIVITIES_OF_NOTARY_PUBLIC'
		},
		bailiffActivities: { id: 'base.bailiff_activities', defaultMessage: 'BAILIFF_ACTIVITIES' },
		consultancyActivities: {
			id: 'base.consultancy_activities',
			defaultMessage: 'CONSULTANCY_ACTIVITIES'
		},
		dealingsInArtWorksOrRelatedActivities: {
			id: 'base.dealings_in_art_works_or_related_activities',
			defaultMessage: 'DEALINGS_IN_ART_WORKS'
		},
		provisionOfRealEstateRepairAndDesign: {
			id: 'base.provision_of_real_estate_repair_and_design',
			defaultMessage: 'PROVISION_OF_REAL_ESTATE_REPAIR_AND_DESIGN'
		},
		searchEngineMarketingAndOptimization: {
			id: 'base.search_engin_marketing_and_optimization',
			defaultMessage: 'SEARCH_ENGINE_MARKETING_AND_OPTIMIZATION'
		},
		otherDigitalMarketingActivities: {
			id: 'base.other_digital_marketing_activities',
			defaultMessage: 'OTHER_DIGITAL_MARKETING_ACTIVITIES'
		},
		taxAdvising: { id: 'base.tax_advising', defaultMessage: 'TAX_ADVISING' },
		tradingInHighValueGoods: {
			id: 'base.trading_in_high_value_goods',
			defaultMessage: 'TRADING_IN_HIGH_VALUE_GOODS'
		},
		tradingInRealEstateAndConstruction: {
			id: 'base.trading_in_real_estate_and_construction',
			defaultMessage: 'REAL_ESTATE'
		},
		tradingInUsedCarsAndTheirSpareParts: {
			id: 'base.trading_in_used_cars_and_their_spare_parts',
			defaultMessage: 'TRADING_IN_USED_CARS_AND_SPARE_PARTS'
		},
		other: { id: 'base.other', defaultMessage: 'OTHER' }
	};

	const translationEntry = Object.entries(values).find(
		([, translation]) => translation.defaultMessage === value
	);

	return translationEntry ? translationEntry[1].id : null;
};

export const sortStages = (stages: FetchedStage[]): Stage[] => {
	const sortedStages = stages.sort((first: FetchedStage, second: FetchedStage) => {
		if (first.position < second.position) return -1;
		if (first.position > second.position) return 1;
		return 0;
	});
	return sortedStages.map((stage: FetchedStage) => {
		return stage?.stage || stage;
	});
};

export function getUnixTimeStampInMs(dateString: string) {
	const date = new Date(dateString);
	return Date.UTC(
		date.getFullYear(),
		date.getMonth(),
		date.getDate(),
		date.getHours(),
		date.getMinutes(),
		date.getSeconds(),
		date.getMilliseconds()
	);
}

export const isLiveEnvironment = () => {
	if (process.env.NODE_ENV === 'production') {
		const environments = ['local', 'dev', 'qa', 'sandbox'];
		return !environments.some(value => window.location.host.includes(value));
	}
	return false;
};

export const isSentryEnabled = () => {
	if (process.env.NODE_ENV === 'production') {
		const environments = ['local', 'dev', 'qa'];
		return !environments.some(value => window.location.host.includes(value));
	}
	return false;
};

export const isMobileUserAgent = () =>
	/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(
		navigator.userAgent
	) ||
	/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
		navigator.userAgent.substr(0, 4)
	);

export const getAppNameFromKey = (
	clientKey: QuestionnaireKey | undefined,
	isBnkPrivate?: boolean
) => {
	switch (clientKey) {
		case QuestionnaireKey.BANKERA_PRIVATE_2023:
			return AppNameEnum.BANKERA;
		case QuestionnaireKey.BANKERA_WALLET:
			if (isBnkPrivate) return AppNameEnum.BANKERA;
			return AppNameEnum.AUTOKYC;
		case QuestionnaireKey.SPECTROCOIN_CORPORATE:
		case QuestionnaireKey.SPECTROCOIN_CORPORATE_BVI:
		case QuestionnaireKey.SPECTROCOIN_EE:
		case QuestionnaireKey.SPECTROCOIN_LT:
			return AppNameEnum.SPECTROCOIN;
		default:
			return AppNameEnum.AUTOKYC;
	}
};

export const getAppUrlFromKey = (
	clientKey: QuestionnaireKey | undefined,
	isBnkPrivate?: boolean
) => {
	switch (clientKey) {
		case QuestionnaireKey.BANKERA_PRIVATE_2023:
			return 'https://bankera.com/get-app';
		case QuestionnaireKey.BANKERA_WALLET:
			if (isBnkPrivate) return 'https://bankera.com/get-app';
			return `${window.location.origin}/get-app`;
		case QuestionnaireKey.SPECTROCOIN_CORPORATE:
		case QuestionnaireKey.SPECTROCOIN_CORPORATE_BVI:
		case QuestionnaireKey.SPECTROCOIN_EE:
		case QuestionnaireKey.SPECTROCOIN_LT:
			return 'https://spectrocoin.com/get-app';
		default:
			return `${window.location.origin}/get-app`;
	}
};

export const getAppUrlsFromKeyOrName = (
	clientKey: QuestionnaireKey | AppNameEnum | null | undefined,
	isBnkPrivate?: boolean
) => {
	switch (clientKey) {
		case QuestionnaireKey.BANKERA_PRIVATE_2023:
			return {
				googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.bankera.wallet',
				appStoreUrl: 'https://apps.apple.com/lt/app/bankera/id1555256201'
			};
		case AppNameEnum.BANKERA:
		case QuestionnaireKey.BANKERA_WALLET:
			if (isBnkPrivate)
				return {
					googlePlayUrl:
						'https://play.google.com/store/apps/details?id=com.bankera.wallet',
					appStoreUrl: 'https://apps.apple.com/lt/app/bankera/id1555256201'
				};
			return {
				googlePlayUrl:
					'https://play.google.com/store/apps/details?id=com.autokyc.android.app',
				appStoreUrl:
					'https://apps.apple.com/lt/app/identity-verification-autokyc/id1551314185'
			};
		case AppNameEnum.SPECTROCOIN:
		case QuestionnaireKey.SPECTROCOIN_CORPORATE:
		case QuestionnaireKey.SPECTROCOIN_CORPORATE_BVI:
		case QuestionnaireKey.SPECTROCOIN_EE:
		case QuestionnaireKey.SPECTROCOIN_LT:
			return {
				googlePlayUrl:
					'https://play.google.com/store/apps/details?id=lt.spectrofinance.spectrocoin.android.wallet',
				appStoreUrl:
					'https://apps.apple.com/lt/app/bitcoin-wallet-by-spectrocoin/id923696089'
			};
		default:
			return {
				googlePlayUrl:
					'https://play.google.com/store/apps/details?id=com.autokyc.android.app',
				appStoreUrl:
					'https://apps.apple.com/lt/app/identity-verification-autokyc/id1551314185'
			};
	}
};

export const getAxiosErrorDefaultMessage = (
	err: unknown,
	includeErrorParameters = false
): string | undefined => {
	if (axios.isAxiosError(err)) {
		const error = err as AxiosError<ErrorInterface>;
		if (error.response) {
			const { errorParameters, defaultMessage } = error.response.data;

			if (includeErrorParameters && errorParameters?.length) {
				const errorString = errorParameters
					.map(param => `${param.key}: ${param.value}`)
					.join('\n');
				return `${defaultMessage}\n${errorString}`;
			}
			return defaultMessage;
		}
	}
	return undefined;
};

export const openUrlInNewTab = (url: string) => {
	if (url) {
		const anchor = document.createElement('a');
		anchor.href = url;
		anchor.target = '_blank';
		anchor.rel = 'noopener noreferrer';
		anchor.click();
	}
};

export const getBoldDeskWidgetScriptSrc = (clientKey: QuestionnaireKey) => {
	const ltTime = new Date().toLocaleString('en-US', {
		timeZone: 'Europe/Vilnius',
		hour: '2-digit',
		hour12: false
	});

	const currentHour = parseInt(ltTime, 10);
	const isDayTime = currentHour >= 8 && currentHour < 22;

	switch (clientKey) {
		case QuestionnaireKey.BANKERA_WALLET:
		case QuestionnaireKey.BANKERA_KYB_2023:
		case QuestionnaireKey.BANKERA_PRIVATE_2023:
		case QuestionnaireKey.BANKERA_MEDIA:
			return isDayTime
				? 'https://help.bankera.com/chatwidget-api/widget/v1/cad54425-0362-4716-9e21-18a69d01376d'
				: 'https://help.bankera.com/widget/8e547a17-1a2e-434d-a66e-5f9979f742e4';
		case QuestionnaireKey.SPECTROCOIN_EE:
		case QuestionnaireKey.SPECTROCOIN_CORPORATE:
		case QuestionnaireKey.SPECTROCOIN_MEDIA:
		case QuestionnaireKey.SPECTROCOIN_IBAN:
		case QuestionnaireKey.SPECTROCOIN_IBAN_2023:
			return isDayTime
				? 'https://help.spectrocoin.com/chatwidget-api/widget/v1/1911eba2-f3fa-4b74-96aa-8ec9f7aa6be0'
				: 'https://help.spectrocoin.com/widget/b993d5b0-3ba2-48fd-9e22-253a3e218d79';
		default:
			return isDayTime
				? 'https://autokyc.bolddesk.com/chatwidget-api/widget/v1/e7c43c25-a1b8-464f-8700-e6fae42dd16d'
				: 'https://autokyc.bolddesk.com/widget/b551a493-6bd8-4177-85a7-cec7606388ed';
	}
};

export const normalizeRequestUrl = (url?: string): string => {
	if (!url) return 'UNKNOWN';

	// External URLs (S3 presigned, CDN, etc.) — just show the host
	if (/^https?:\/\//.test(url)) {
		try {
			return `{external}:${new URL(url).hostname}`;
		} catch {
			return '{external}';
		}
	}

	return (
		url
			.replace(/\/verifications\/[^/?]+/, '/verifications/{key}')
			.replace(/\/corporate-verifications\/[^/?]+/, '/corporate-verifications/{key}')
			.replace(/\/clients\/[^/?]+/, '/clients/{key}')
			.replace(/\/supporting-documents\/[^/?]+/, '/supporting-documents/{id}')
			.replace(/\/tin\/[^/?]+/, '/tin/{country}')
			// Fallback: only segments containing at least one digit
			// catches mnaf7t2d, abc123 — skips verifications, penultimate, questions
			.replace(/\/(?=[a-zA-Z0-9]*\d)[a-zA-Z0-9]{6,}(\/|$)/g, '/{id}$1')
	);
};
